import React, { useCallback, useMemo } from 'react';
import { Anchor, Box, ClockIcon, FeeLabel, PlusIcon, Text, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import PaymentTypeIcon from '@/components/elements/PaymentTypeIcon';
import {
  PaymentMethod,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { AllowedPaymentMethodConfig, PaymentMethodType } from '@/modules/user/user.types';

import { AddCardPaymentMethod } from './AddCardPaymentMethod';
import { PaymentMethodLayout, PaymentMethodTiming } from './paymentMethodLayout.types';

const IconWrapper = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  title?: string | React.ReactNode;
  description?: string;
  paymentType?: PaymentMethodType;
  add?: () => void;
  removePaymentMethod?: () => void;
  editPaymentMethod?: () => void;
  updateDefaultPaymentMethod?: (paymentMethodId: number) => void;
  addTitle?: string;
  paymentMethods?: PaymentMethod[];
  onlyAddingMode?: boolean;
  hideIcon?: boolean;
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  isUnwrapped?: boolean;
  isRejectionFlow?: boolean;
  paymentMethodLayout?: PaymentMethodLayout;
  cardFooterRenderWrapper?: (paymentMethodType?: PaymentMethodType) => React.ReactNode;
}

const AddCard: React.FC<Props> = ({
  title,
  description,
  add,
  removePaymentMethod,
  editPaymentMethod,
  updateDefaultPaymentMethod,
  addTitle,
  paymentType,
  paymentMethods,
  onlyAddingMode,
  hideIcon,
  allowedPaymentMethodConfigs,
  isUnwrapped,
  isRejectionFlow,
  paymentMethodLayout,
  cardFooterRenderWrapper,
}) => {
  const { isMobile } = useResponsive();

  const hideAddBtn = useMemo(
    () => paymentMethods && paymentMethods.length && paymentType === PaymentMethodType.CHECK,
    [paymentMethods, paymentType]
  );

  const isPendingDirectDeposit = useCallback(
    (methodId?: string) => {
      const currentPaymentMethod = paymentMethods?.find((method) => method?.id === methodId);
      return (
        currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
        currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL
      );
    },
    [paymentMethods]
  );

  const isFailedDirectDeposit = useCallback(
    (methodId?: string) => {
      const currentPaymentMethod = paymentMethods?.find((method) => method?.id === methodId);
      return (
        currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
        currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED
      );
    },
    [paymentMethods]
  );

  const paymentEmployeeFee = useMemo(() => {
    const fee = allowedPaymentMethodConfigs?.find(
      (item) => item?.paymentMethodType === paymentType
    )?.paymentEmployeeFee;
    return fee !== undefined ? String(fee) : undefined;
  }, [paymentType, allowedPaymentMethodConfigs]);

  const processingTimeBadge = (
    <Box
      direction="row"
      align="center"
      gap="spacing4"
      border={{ color: 'border2', size: 'small' }}
      pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
      round="xsmall"
    >
      <ClockIcon size="16px" color="iconSecondary" />
      <Text size="medium" color="textBody" weight={400}>
        {paymentType ? PaymentMethodTiming[paymentType] : ''}
      </Text>
    </Box>
  );

  if (isUnwrapped) {
    return (
      <Box
        data-testid={`add_card_unwrapped_${paymentType}`}
        round="container1Round"
        border={{ color: 'border2', size: 'small' }}
        background="canvas"
        style={{ transition: 'border-color 0.2s ease-out' }}
      >
        <Box
          pad={{ top: 'spacing24', horizontal: 'spacing24', bottom: 'spacing12' }}
          gap="spacing10"
          align="start"
        >
          <Box direction="row" align="center" gap="spacing12">
            <PaymentTypeIcon
              paymentType={paymentType}
              size="48px"
              wrap={(icon) => (
                <Box
                  width="48px"
                  height="48px"
                  flex={{ shrink: 0 }}
                  align="center"
                  justify="center"
                  style={{ position: 'relative' }}
                >
                  {icon}
                </Box>
              )}
            />
            <Text
              size="16px"
              weight={700}
              color="textBody"
              style={{ lineHeight: '22px' }}
              data-testId={`add_card_title_${paymentType}`}
            >
              {title}
            </Text>
          </Box>

          {paymentMethods?.map((method) => (
            <AddCardPaymentMethod
              key={method.id}
              method={method}
              disableOptions={isPendingDirectDeposit(method?.id) || isFailedDirectDeposit(method?.id)}
              removePaymentMethod={removePaymentMethod}
              editPaymentMethod={editPaymentMethod}
              updateDefaultPaymentMethod={updateDefaultPaymentMethod}
              hideIcon={hideIcon}
              showPlaidStatus
              allowedPaymentMethodConfigs={allowedPaymentMethodConfigs}
              isRejectionFlow={isRejectionFlow}
              paymentMethodLayout={paymentMethodLayout}
            />
          ))}

          {!hideAddBtn && (
            <Anchor
              color="textAccent"
              size={isMobile ? 'large' : 'medium'}
              weight={isMobile ? 700 : 400}
              onClick={add}
            >
              <Box
                direction="row"
                align="center"
                gap="spacing8"
                pad={{ vertical: 'spacing8' }}
                style={{ borderRadius: '4px', alignSelf: 'stretch' }}
              >
                <PlusIcon size="16px" color="textAccent" />
                <Text size={isMobile ? 'large' : 'medium'} color="textAccent" weight={isMobile ? 700 : 400}>
                  {addTitle}
                </Text>
              </Box>
            </Anchor>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      round="container1Round"
      border={{ color: 'border2', size: 'small' }}
      background="canvas"
      style={{ transition: 'border-color 0.2s ease-out' }}
    >
      <Box pad="spacing24" gap="spacing12">
        <Box gap="spacing4">
          <Box direction="row" align="center" gap="spacing6">
            <PaymentTypeIcon
              paymentType={paymentType}
              size="24px"
              wrap={(icon) => <IconWrapper>{icon}</IconWrapper>}
            />
            <Text size="xxlarge" weight={700} color="textTitle" data-testId={`add_card_title_${paymentType}`}>
              {title}
            </Text>
          </Box>

          {description && (
            <Text size="medium" color="textBody" weight={400}>
              {description}
            </Text>
          )}
        </Box>

        <Box direction="row" align="center" gap="spacing4" wrap style={{ rowGap: '4px' }}>
          <FeeLabel amount={paymentEmployeeFee} testId={paymentType} size="medium" withBorder />
          {processingTimeBadge}
        </Box>

        {!onlyAddingMode && paymentMethods && paymentMethods.length > 0 && (
          <Box background="module" round="container2Round" pad="spacing4" gap="spacing4">
            {paymentMethods.map((method) => (
              <AddCardPaymentMethod
                key={method.id}
                method={method}
                disableOptions={isPendingDirectDeposit(method?.id) || isFailedDirectDeposit(method?.id)}
                removePaymentMethod={removePaymentMethod}
                editPaymentMethod={editPaymentMethod}
                updateDefaultPaymentMethod={updateDefaultPaymentMethod}
                hideIcon={hideIcon}
                showPlaidStatus
                allowedPaymentMethodConfigs={allowedPaymentMethodConfigs}
                isRejectionFlow={isRejectionFlow}
                paymentMethodLayout={paymentMethodLayout}
              />
            ))}
          </Box>
        )}

        {!hideAddBtn && (
          <Anchor
            color="textAccent"
            size={isMobile ? 'large' : 'medium'}
            weight={isMobile ? 700 : 400}
            onClick={add}
          >
            {addTitle}
          </Anchor>
        )}

        {cardFooterRenderWrapper?.(paymentType)}
      </Box>
    </Box>
  );
};

export default AddCard;
